import * as THREE from 'three';
import swatchMaterialTemplates from '@/mixins/swatchMaterialGroups/swatchMaterialTemplates';
import componentConfigGroup from '@/mixins/componentConfigGroup';
import merchandisingRules from '@/mixins/geometryUpdates/merchandisingRules';
import { decoStitch } from '@/data/constants';

export default {
  mixins: [swatchMaterialTemplates, componentConfigGroup, merchandisingRules],
  computed: {
    pdm() {
      return this.$store.state.utils.pdm;
    },
    decoStitchObj() {
      return this.$store.state.utils.pdm.components.decoStitch;
    },
    enablePerformanceLogging() {
      return this.$store.state.utils.queryParams.enablePerformanceLogging || this.$store.state.utils.configuration.enablePerformanceLogging;
    },
    initialTime() {
      return this.$store.state.utils.initialTime;
    },
  },
  methods: {
    /**
     * @function applySwatch
     * @param {String} componentName - Name of the component to apply new swwatch.
     * @param {Number} swatchID - Id of the swatch to apply to the component
     * @param {Array} componentGroup - grouped components to apply a swatch, ex: (foxing, foxing-tip, etc.)
     * Using the library sceneHelpers, change the swatch texture of the 3D model.
     */
    applySwatch(componentName, swatchId, componentGroup, color = null) {
      return new Promise ((resolve, reject) => {
        const swatchObj = this.$store.getters["swatches/returnSwatchObj"]({ swatchId });

        this.updateComponentConfigGroup({swatchId, componentName, swatchObj, componentGroup, color}).then(()=>{
          this.removeDecoStitch(componentName, swatchId);

          if (swatchObj.ugc) {
            this.$eventBus.$emit('apply-ugc-swatch', { component: componentName, ugc: swatchObj.ugc, resolve, reject });
            return resolve();
          }
          swatchId = Number(swatchId);
          if (swatchObj.material_type && swatchObj.material_type === 'graphic') {
            //skip sceneHelper for Apparel graphics, handled by GraphicController
            return resolve();
          }

          this.getSwatchMaterialTemplate({swatchObj}).then(response => {
          const templateMaterial = response && response.data;
          this.$model3D.sceneHelpers.changeSwatch({ swatchId, componentGroup, componentName, templateMaterial }, () => {
            if (swatchObj.ugc) {
              this.$eventBus.$emit('re-apply-decostitch');
            }
            this.applyMerchRulesAfterSwatch({swatch: swatchObj, componentName});
            if(this.enablePerformanceLogging)
              console.log(`Swatch ${swatchId} Applied to ${componentName}: `, new Date().getTime() / 1000 - this.initialTime);
            resolve();
            });
          }).catch(reject);
        });
      });
    },
    /**
      * @function applyMaterial
      * @param {Object} component - THREE.Mesh
      * @param {Object} options
      * @param {String} material type of THREE.<Material>
      * @param {String} type of shader files: Configurator.shaders
      * Create or update material for component.
      */
    applyMaterial(component, options, meshType, shadersType) {
      if (component.material && component.material.isCustomsShaderMaterial) {
        this.$model3D.sceneHelpers.updateMaterial({material: component.material, options});
      } else {
        component.material = this.$model3D.sceneHelpers.createMaterial({
          mesh_type: meshType,
          options,
          componentName: component.name,
          shadersType
        });
      }
      component.material.uvState = shadersType;
      return component.material;
    },
    /**
      * @function removeDecoStitch
      * @param {string} componentName - String value representing the componenet name. Eg: "vamp"
      * @param {Object} swatchId - id of the swatch thats being applied to the component
      * Removes the deco stitch component from the 3D model, recipe and selected swatch
      * when the user applies a color swatch to the deco stitch component.
      */
    removeDecoStitch(componentName, swatchId){
      const swatchObj = this.$store.getters["swatches/returnSwatchObj"]({ swatchId});
      const removeDecoStitch = !swatchObj?.geometry_updates?.includes(decoStitch);
      if (removeDecoStitch && this.$store.state.geometryUpdates.decostitchApplied) {
        const swatchGroup = this.$store.getters["swatches/returnSwatchGroupName"]({ swatch: swatchObj });
        const swatchSubgroup = this.$store.getters["swatches/returnSwatchSubgroupName"]({ swatch: swatchObj });
        if (componentName !== this.decoStitchObj.options.componentToHide) return false;

        this.showComponent();
        this.$store.commit("swatches/SET_SWATCHES_SELECTED", {
          component: componentName,
          data: {
            swatchId: swatchId.toString(),
            group: swatchGroup,
            subgroup: swatchSubgroup
          }
        });

      }
    },
  }
};
